import { useEffect, useState, useCallback } from "react";
import { FaSave, FaSpinner, FaPaperPlane } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./ManageAdmins.css";

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    from_email: "",
    from_name: "CIAL Airport Robot Portal",
    use_tls: true
};

function EmailConfiguration() {

    const [form, setForm] = useState(emptyForm);

    const [isConfigured, setIsConfigured] = useState(false);

    const [passwordIsSet, setPasswordIsSet] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const [isSaving, setIsSaving] = useState(false);

    const [testEmail, setTestEmail] = useState("");

    const [isSendingTest, setIsSendingTest] = useState(false);

    const load = useCallback(async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings`);

            const data = await res.json();

            setForm({
                smtp_host: data.smtp_host || "",
                smtp_port: data.smtp_port || 587,
                smtp_username: data.smtp_username || "",
                smtp_password: "",
                from_email: data.from_email || "",
                from_name: data.from_name || "CIAL Airport Robot Portal",
                use_tls: data.use_tls !== false
            });

            setIsConfigured(!!data.is_configured);

            setPasswordIsSet(!!data.smtp_password_is_set);

        } catch (err) {

            console.error("Failed to load email settings:", err);

            toast.error("Unable to load email settings.");

        } finally {

            setIsLoading(false);

        }

    }, []);

    useEffect(() => {

        load();

    }, [load]);

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const handleSave = async () => {

        if (!form.smtp_host || !form.from_email) {

            toast.warn("SMTP host and From email are required.");
            return;

        }

        setIsSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({
                    ...form,
                    smtp_port: Number(form.smtp_port) || 587
                })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

                load();

            } else {

                toast.error(data.message || data.detail || "Failed to save email settings.");

            }

        } catch (err) {

            console.error("Failed to save email settings:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsSaving(false);

        }

    };

    const handleSendTest = async () => {

        if (!testEmail.trim()) {

            toast.warn("Enter a recipient email for the test.");
            return;

        }

        setIsSendingTest(true);

        try {

            const res = await fetch(`${API_URL}/portal/email-settings/test`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({ to_email: testEmail.trim() })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

            } else {

                toast.error(data.message || data.detail || "Failed to send test email.");

            }

        } catch (err) {

            console.error("Failed to send test email:", err);

            toast.error("Something went wrong while sending the test email.");

        } finally {

            setIsSendingTest(false);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Email Configuration</h2>
                    <p className="page-subtitle">
                        SuperAdmin only. These SMTP settings power password-reset emails and the
                        "Send Email" feature. {isConfigured
                            ? "Currently configured and ready to send."
                            : "Not configured yet - emails won't send until this is filled in."}
                    </p>
                </div>

            </div>

            {

                isLoading ? (

                    <p className="empty-note"><FaSpinner className="spin" /> Loading...</p>

                ) : (

                    <div className="inline-form-container">

                        <div className="inline-form-grid">

                            <input
                                name="smtp_host"
                                placeholder="SMTP Host (e.g. smtp.gmail.com)"
                                value={form.smtp_host}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_port"
                                type="number"
                                placeholder="SMTP Port (e.g. 587)"
                                value={form.smtp_port}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_username"
                                placeholder="SMTP Username"
                                value={form.smtp_username}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="smtp_password"
                                type="password"
                                placeholder={passwordIsSet ? "•••••• (saved - leave blank to keep)" : "SMTP Password"}
                                value={form.smtp_password}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="from_email"
                                placeholder="From Email (e.g. noreply@yourdomain.com)"
                                value={form.from_email}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="from_name"
                                placeholder="From Name"
                                value={form.from_name}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <label className="checkbox-field">
                                <input
                                    type="checkbox"
                                    name="use_tls"
                                    checked={!!form.use_tls}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Use TLS (recommended - most providers require this on port 587)
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="save-btn"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Settings</>}
                            </button>

                        </div>

                        <div className="drawer-section" style={{ marginTop: "24px" }}>

                            <h4>Send a test email</h4>

                            <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "10px" }}>
                                Save your settings first, then confirm they actually work by sending
                                yourself a test email.
                            </p>

                            <div style={{ display: "flex", gap: "10px" }}>

                                <input
                                    placeholder="you@example.com"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    disabled={isSendingTest}
                                    style={{ flex: 1, padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px" }}
                                />

                                <button
                                    type="button"
                                    className="add-btn"
                                    onClick={handleSendTest}
                                    disabled={isSendingTest}
                                >
                                    {isSendingTest ? <><FaSpinner className="spin" /> Sending...</> : <><FaPaperPlane /> Send Test</>}
                                </button>

                            </div>

                        </div>

                    </div>

                )

            }

        </div>

    );

}

export default EmailConfiguration;
